import React, { useMemo, useEffect } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { formatCurrency } from '../utils/formatters';

// Register Chart.js components
ChartJS.register(...registerables);

const SalesCharts = ({ sales = [] }) => {

    // Cleanup charts on unmount
    useEffect(() => {
        return () => {
            // Destroy all charts when component unmounts
            Object.values(ChartJS.instances).forEach(chart => {
                chart.destroy();
            });
        };
    }, []);

    // Memoized data for Sales by Location chart
    const salesByLocationData = useMemo(() => {
        if (!Array.isArray(sales)) return {
            labels: ['No Data'],
            datasets: [{
                label: 'Sales Amount by Location',
                data: [1],
                backgroundColor: ['rgba(200, 200, 200, 0.7)'],
                borderColor: ['rgba(200, 200, 200, 1)'],
                borderWidth: 1,
            }],
        };

        const locationSales = sales.reduce((acc, sale) => {
            acc[sale.location] = (acc[sale.location] || 0) + sale.amount;
            return acc;
        }, {});
        const labels = Object.keys(locationSales);
        const data = Object.values(locationSales);
        return {
            labels: labels.length > 0 ? labels : ['No Data'],
            datasets: [{
                label: 'Sales Amount by Location',
                data: data.length > 0 ? data : [1],
                backgroundColor: labels.length > 0 ? ['rgba(54, 162, 235, 0.7)', 'rgba(255, 159, 64, 0.7)'] : ['rgba(200, 200, 200, 0.7)'],
                borderColor: labels.length > 0 ? ['rgba(54, 162, 235, 1)', 'rgba(255, 159, 64, 1)'] : ['rgba(200, 200, 200, 1)'],
                borderWidth: 1,
            }],
        };
    }, [sales]);

    // Memoized data for Sales by Product chart
    const salesByProductData = useMemo(() => {
        if (!Array.isArray(sales)) return {
            labels: ['No Data'],
            datasets: [{
                label: 'Sales Amount by Product',
                data: [0],
                backgroundColor: ['rgba(200, 200, 200, 0.7)'],
                borderColor: ['rgba(200, 200, 200, 1)'],
                borderWidth: 1,
            }],
        };

        const productSales = sales.reduce((acc, sale) => {
            // Use product name from the joined products data, fallback to productType for backward compatibility
            const productName = sale.product?.name || sale.products?.name || sale.productType || 'Unknown Product';
            acc[productName] = (acc[productName] || 0) + sale.amount;
            return acc;
        }, {});
        const labels = Object.keys(productSales);
        const data = Object.values(productSales);
        return {
            labels: labels.length > 0 ? labels : ['No Data'],
            datasets: [{
                label: 'Sales Amount by Product',
                data: data.length > 0 ? data : [0],
                backgroundColor: ['rgba(75, 192, 192, 0.7)', 'rgba(153, 102, 255, 0.7)', 'rgba(255, 205, 86, 0.7)', 'rgba(255, 99, 132, 0.7)', 'rgba(54, 162, 235, 0.7)'],
                borderColor: ['rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)', 'rgba(255, 205, 86, 1)', 'rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)'],
                borderWidth: 1,
            }],
        };
    }, [sales]);

    // Common chart options
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.label || '';
                        if (label) label += ': ';
                        if (context.parsed?.y !== undefined) label += formatCurrency(context.parsed.y);
                        else if (context.parsed !== undefined) label += formatCurrency(context.parsed);
                        return label;
                    }
                }
            }
        },
    };

    // Specific options for the bar chart
    const barChartOptions = {
        ...chartOptions,
        scales: { 
            y: { 
                beginAtZero: true, 
                ticks: { callback: value => (value >= 1000 ? `${value / 1000}k` : value) }
            }
        },
        plugins: { 
            ...chartOptions.plugins, 
            legend: { display: false } 
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Sales by Location Chart (Doughnut) */}
            <div className="bg-white p-4 md:p-6 rounded-2xl border border-slate-200">
                <h3 className="text-base font-semibold text-slate-900 mb-3">Sales by Location</h3>
                <div className="relative h-56 md:h-72 w-full max-w-[500px] mx-auto">
                    <Doughnut data={salesByLocationData} options={chartOptions} />
                </div>
            </div>
            {/* Sales by Product Chart (Bar) */}
            <div className="bg-white p-4 md:p-6 rounded-2xl border border-slate-200">
                <h3 className="text-base font-semibold text-slate-900 mb-3">Sales by Product</h3>
                <div className="relative h-56 md:h-72 w-full max-w-[500px] mx-auto">
                    <Bar data={salesByProductData} options={barChartOptions} />
                </div>
            </div>
        </div>
    );
};

export default SalesCharts; 